import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TimeIcon from '@mui/icons-material/AccessTime';
import { ShiftSchedule, Worker } from '../../types/worker';
import { fetchShifts, addShift, fetchWorkers } from '../../services/workerService';

interface ShiftSchedulingTabProps {
  farmId: string;
}

const ShiftSchedulingTab: React.FC<ShiftSchedulingTabProps> = ({ farmId }) => {
  const [shifts, setShifts] = useState<ShiftSchedule[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newShift, setNewShift] = useState<Partial<ShiftSchedule>>({
    farmId,
    startTime: '',
    endTime: '',
    roleTask: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [farmId]);

  const loadData = async () => {
    try {
      const [shiftsData, workersData] = await Promise.all([
        fetchShifts(farmId),
        fetchWorkers(farmId)
      ]);
      setShifts(shiftsData);
      setWorkers(workersData);
    } catch (error) {
      console.error('Error loading shift data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddShift = async () => {
    try {
      if (!newShift.workerId || !newShift.startTime || !newShift.endTime) return;
      
      await addShift({ ...newShift, farmId });
      setOpen(false);
      loadData();
      setNewShift({ farmId, startTime: '', endTime: '', roleTask: '', notes: '' });
    } catch (error) {
      console.error('Error adding shift:', error);
    }
  };

  const getWorkerName = (id: string) => {
    return workers.find(w => w.id === id)?.name || 'Unknown Worker';
  };

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">Shift Schedule</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          Assign Shift
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Worker</TableCell>
              <TableCell>Start Time</TableCell>
              <TableCell>End Time</TableCell>
              <TableCell>Task/Role</TableCell>
              <TableCell>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shifts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">No shifts scheduled</TableCell>
              </TableRow>
            ) : (
              shifts.map((shift) => (
                <TableRow key={shift.id}>
                  <TableCell sx={{ fontWeight: 500 }}>{getWorkerName(shift.workerId)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TimeIcon fontSize="small" color="action" />
                      {formatDateTime(shift.startTime)}
                    </Box>
                  </TableCell>
                  <TableCell>{formatDateTime(shift.endTime)}</TableCell>
                  <TableCell>
                    {shift.roleTask && <Chip label={shift.roleTask} size="small" />}
                  </TableCell>
                  <TableCell>{shift.notes}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign New Shift</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                label="Worker"
                fullWidth
                value={newShift.workerId || ''}
                onChange={(e) => setNewShift({ ...newShift, workerId: e.target.value })}
              >
                {workers.map((worker) => (
                  <MenuItem key={worker.id} value={worker.id}>
                    {worker.name} ({worker.role})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Start Time"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newShift.startTime}
                onChange={(e) => setNewShift({ ...newShift, startTime: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="End Time"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newShift.endTime}
                onChange={(e) => setNewShift({ ...newShift, endTime: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Task / Role"
                fullWidth
                placeholder="e.g. Morning Feed, Harvest"
                value={newShift.roleTask || ''}
                onChange={(e) => setNewShift({ ...newShift, roleTask: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                fullWidth
                multiline
                rows={2}
                value={newShift.notes || ''}
                onChange={(e) => setNewShift({ ...newShift, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddShift}>Assign</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShiftSchedulingTab;
